import {useState, useContext, useEffect} from 'react';
import {Alert, Button, Divider, FormControl, Stack, Typography, InputAdornment, IconButton} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {AuthBase, AuthHeading, AuthLogo, UseGoogleButton, AutoFillAwareTextField} from '../base';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import {googleAuthEnabled} from '../constants';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import '../../../App.css';

export const LoginPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState(params.get('token') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {loginUser, setUser, unauthorized, setUnauthorized, setIsGuestWorkflow, getPostAuthNavigation} =
    useContext(AuthContext);

  const formValidated = email !== '' && password !== '';

  // Updated navigateNext function
  const navigateNext = () => {
    const nextRoute = getPostAuthNavigation();
    navigate(nextRoute);
  };

  useEffect(() => {
    const urlToken = params.get('token');
    const urlEmail = params.get('email');
    const redirect = params.get('redirect');

    if (urlToken) {
      setToken(urlToken);
      sessionStorage.setItem('token', urlToken);

      // Mark as guest workflow if token exists
      setIsGuestWorkflow(true);
    }

    // Pre-fill email if provided in URL
    if (urlEmail) {
      setEmail(urlEmail);
    }

    // Clear password field
    setPassword('');
  }, [params, setIsGuestWorkflow]);

  const handleLogin = async () => {
    try {
      const status = await loginUser(email, password, 'email');
      if (status === 'ok') {
        navigateNext();
      }
    } catch (error) {
      setUnauthorized(true);
      console.error('Error during login process:', error);
    }
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
    <AuthBase>
      <Stack spacing={2} alignItems="start" justifyContent="stretch" sx={{width: 1}}>
        <AuthLogo />
        <AuthHeading>Sign in to your account</AuthHeading>

        {googleAuthEnabled && (
          <>
            <Divider sx={{width: 1, color: 'gray'}} />
            <UseGoogleButton
              setUser={setUser}
              factor="email"
              navigate={navigateNext}
              setUnauthorized={setUnauthorized}
              token={token}
            />
            <Divider sx={{width: 1, color: 'var(--aimdFlowPrimaryColor)'}}>
              <Typography>Or, sign in with email</Typography>
            </Divider>
          </>
        )}

        {unauthorized ? <Alert severity="error">Incorrect username or password.</Alert> : ''}

        <FormControl fullWidth>
          <AutoFillAwareTextField
            customSx={true}
            isInputBgColorOnFocus={true}
            placeholder="Email Address"
            variant="outlined"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
            }}
            fullWidth
          />
        </FormControl>

        <FormControl fullWidth>
          <AutoFillAwareTextField
            customSx={true}
            isInputBgColorOnFocus={true}
            autoComplete="new-password"
            placeholder="Password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    ) : (
                      <Visibility sx={{color: 'var(--aimdFlowPrimaryColor)'}} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '40px',
                color: 'var(--aimdFlowSecondaryDarkTextColor)',
                fontWeight: 500,
                fontSize: '16px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid var(--aimdFlowPrimaryColor)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                },
              },
            }}
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
          />
        </FormControl>

        <Button
          disabled={!formValidated}
          onClick={handleLogin}
          fullWidth
          variant="outlined"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: '40px',
            height: '65px',
            fontWeight: 600,
            fontSize: '20px',
            background: 'var(--aimdLinearGradientBtnColor)',
            color: '#fff',
            '&:hover': {
              background: 'rgb(141, 209, 210)',
              borderColor: 'rgb(141, 209, 210)',
            },
            '&.Mui-disabled': {
              background: 'rgb(161,229,230)',
              color: 'rgba(255, 255, 255, 0.70)',
            },
          }}
        >
          Continue with email
        </Button>

        <Typography sx={{width: 1, textAlign: 'center', color: '#000', fontWeight: 500}}>
          Don't have an account yet?
          <Link
            to="/register"
            style={{
              textDecorationColor: 'var(--aimdFlowSecondaryDarkTextColor)',
              color: 'var(--aimdFlowSecondaryDarkTextColor)',
            }}
          >
            <Typography sx={{fontWeight: 500, fontSize: '17px'}}>Get started here.</Typography>
          </Link>
        </Typography>

        <Typography sx={{width: 1, textAlign: 'center'}}>
          <Link
            to="/forgot-password"
            style={{
              textDecorationColor: 'var(--aimdFlowSecondaryDarkTextColor)',
              color: 'var(--aimdFlowSecondaryDarkTextColor)',
              fontWeight: 500,
              fontSize: '17px',
            }}
          >
            Forgot your password?
          </Link>
        </Typography>
      </Stack>
    </AuthBase>
  );
};
